import React from 'react'
import { bindActionCreators, compose } from 'redux'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import ProTag from '../../components/general/ProTag'
import AccountNotifications from './AccountNotifications'
import { slugifyUsername } from '../../helper/string'
import Icon from '../../components/general/Icon'
import Separator from '../../components/button/Separator'
import { desktop, isStandalone, VIEWPORT_DESKTOP, VIEWPORT_MOBILE } from '../../styles/media'
import references from '../../data/references'
import { install } from '../../helper/pwa'
import DropDown, { DropDownLink } from '../../components/button/DropDown'
import { logout } from '../../state/actionCreators/app'
import { setReference } from '../../state/actions/app'
import translate from '../../helper/translate'
import Avatar from '../../components/user/Avatar'
import { getHelpLink } from '../../helper/links'
import Link from '../../components/router/Link'

const UserDropDown = ({ avatar, button, logout, pwaInstallEvent, role, setReference, subscribed, t, title, transparent, userLanguage, username, viewport }) => (
	<DropDown
		button={button || (
			<UserLink transparent={transparent}>
				<Avatar width={40} avatar={avatar || 'avatar-male'} />
				<span>{username}</span>
				{subscribed === 'pro' && <ProTag title={title} />}

				<AccountNotifications type="account" />
			</UserLink>
		)}
		top={56}
		right={0}
		width={240}
	>
		{onBeforeNavigate => (
			<>
				<StyledDropDownLink
					avatar={viewport === VIEWPORT_MOBILE}
					to={`/user/${slugifyUsername(username)}`}
					onBeforeNavigate={onBeforeNavigate}
				>
					{viewport === VIEWPORT_DESKTOP ? (
						<>
							<Icon name="user" />
							<span>{t('general.myProfile')}</span>
						</>
					) : (
						<>
							<Avatar width={40} avatar={avatar || 'avatar-male'} />
							<span>{username}</span>
							{subscribed === 'pro' && <ProTag title={title} />}
						</>
					)}
				</StyledDropDownLink>
				<StyledDropDownLink to={`/user/${slugifyUsername(username)}/starred`} onBeforeNavigate={onBeforeNavigate}>
					<Icon name="star" />
					<span>{t('general.favorites')}</span>
				</StyledDropDownLink>

				<Separator />

				{!subscribed && (
					<>
						<StyledDropDownLink
							to="/product/collection-manager"
							onBeforeNavigate={() => {
								onBeforeNavigate()
								setReference(references.USER_MENU)
							}}
						>
							<Icon name="archive" />
							<span>{t('general.collectionManager')}</span>
						</StyledDropDownLink>
						<StyledDropDownLink
							highlight
							to="/pricing"
							onBeforeNavigate={() => {
								onBeforeNavigate()
								setReference(references.USER_MENU)
							}}
						>
							<Icon name="star" />
							<span>cardcluster <ProTag /></span>
						</StyledDropDownLink>

						{!isStandalone() && (
							<StyledDropDownLink to="/install" onBeforeNavigate={onBeforeNavigate}>
								<Icon name="device-mobile" />
								<span>{t('general.installApp')}</span>
							</StyledDropDownLink>
						)}
						<Separator />
					</>
				)}

				<StyledDropDownLink to="/settings" onBeforeNavigate={onBeforeNavigate}>
					<Icon name="cog" />
					<span>{t('general.settings')}</span>
					<AccountNotifications type="settings" />
				</StyledDropDownLink>
				{role === 'admin' || role === 'mod' ? (
					<StyledDropDownLink to="/admin" onBeforeNavigate={onBeforeNavigate}>
						<Icon name="terminal" />
						<span>{t('general.admin')}</span>
						<AccountNotifications type="admin" />
					</StyledDropDownLink>
				) : null}

				<StyledDropDownLink external externalNoIcon to={getHelpLink(userLanguage)} onBeforeNavigate={onBeforeNavigate}>
					<Icon name="question-mark-circle" />
					<span>{t('general.help')}</span>
				</StyledDropDownLink>
				<StyledDropDownLink to="/support" onBeforeNavigate={onBeforeNavigate}>
					<Icon name="mail" />
					<span>{t('general.support')}</span>
				</StyledDropDownLink>

				<Separator />
				<StyledDropDownLink red onClick={() => logout(t, false)} onBeforeNavigate={onBeforeNavigate}>
					<Icon name="logout" />
					<span>{t('general.logout')}</span>
				</StyledDropDownLink>
			</>
		)}
	</DropDown>
)

UserDropDown.propTypes = {
	avatar: PropTypes.string,
	button: PropTypes.any,
	logout: PropTypes.func.isRequired,
	pwaInstallEvent: PropTypes.any,
	role: PropTypes.string.isRequired,
	setReference: PropTypes.func.isRequired,
	subscribed: PropTypes.string,
	t: PropTypes.func.isRequired,
	title: PropTypes.string,
	transparent: PropTypes.string,
	userLanguage: PropTypes.string.isRequired,
	username: PropTypes.string.isRequired,
	viewport: PropTypes.string.isRequired
}

UserDropDown.defaultProps = {
	avatar: null,
	button: null,
	pwaInstallEvent: null,
	subscribed: null,
	title: null,
	transparent: null
}

const StyledDropDownLink = styled(DropDownLink)`
	${desktop`
		svg {
			margin-left: 0.25rem;
		}
	`}
`

const UserLink = styled(Link)`
	border-radius: 8px;
	font-weight: 500;
	height: 48px;
	padding: 0 0.75rem;
	text-decoration: none;
	transition: 0.1s;

	align-items: center;
	display: flex;
	gap: 0.5rem;

	> span {
		white-space: nowrap;
		max-width: 250px;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	img {
		border-radius: 50%;
		height: 34px;
		width: 34px;
	}

	${props => props.active && `
		background: ${props.theme.background};
		color: ${props.theme.text} !important;

		${props.theme.id === 'light' && `
			box-shadow: ${props.theme.shadowSearchHover};
		`}
	`}

	${desktop`
		&:hover {
			background: ${props => props.theme.id === 'dark' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)'};
			color: ${props => props.theme.text};

			${props => props.theme.id === 'light' && `
				box-shadow: ${props.theme.shadowSearchHover};
			`}
		}
	`}
`

const enhance = compose(
	connect(state => ({
		avatar: state.persist.user.avatar,
		pwaInstallEvent: state.app.pwaInstallEvent,
		role: state.persist.user.role,
		subscribed: state.persist.user.subscribed,
		title: state.persist.user.title,
		userLanguage: state.persist.language,
		username: state.persist.user.username,
		viewport: state.app.viewport
	}), dispatch => bindActionCreators({
		logout,
		setReference
	}, dispatch)),
	translate('general'),
	withRouter
)

export default enhance(UserDropDown)
